import { useMemo, useState } from 'react';
import { InvoiceExtraType } from 'api/model';
import type {
  InvoiceExtraDefaultValueDto,
  UpdateInvoiceExtraDefaultValueModel,
} from 'api/model';
import {
  useGlobalSettingsGetInvoiceExtraDefaultValues,
  useGlobalSettingsUpdateInvoiceExtraDefaultValue,
} from 'api/global-settings/global-settings';
import useTranslations from 'contexts/basicData/useTranslations';
import useMe from 'contexts/authentication/useMe';
import Roles, { includesRoles } from 'constants/Roles';
import styled from 'styled-components';
import { faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showException } from 'utils/exception-helper';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import Input from 'components/inputs/Input';
import TextButton from 'components/inputs/TextButton';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CellRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

interface DefaultValueRow {
  type: InvoiceExtraType;
  name: string;
  value: number;
}

const allInvoiceExtraTypes: InvoiceExtraType[] = Object.values(
  InvoiceExtraType
).filter((v): v is InvoiceExtraType => typeof v === 'number');

const InvoiceExtraDefaultValueSettings = () => {
  const me = useMe();
  const isSuperAdmin = includesRoles([Roles.SuperAdmin], me?.roles);
  const { invoiceExtraTypes: invoiceExtraTypeTranslations } = useTranslations();
  const { data: defaults, isLoading, refetch } =
    useGlobalSettingsGetInvoiceExtraDefaultValues();
  const updateCall = useGlobalSettingsUpdateInvoiceExtraDefaultValue();

  const [localEdits, setLocalEdits] = useState<Record<number, number>>({});
  const [savingRows, setSavingRows] = useState<Set<number>>(new Set());

  const getDefault = (
    type: InvoiceExtraType
  ): InvoiceExtraDefaultValueDto | undefined =>
    defaults?.find((d) => d.type === type);

  const rows = useMemo((): DefaultValueRow[] => {
    return allInvoiceExtraTypes.map((t) => {
      const row = getDefault(t);
      return {
        type: t,
        name:
          (invoiceExtraTypeTranslations as Record<string, string>)?.[
            String(t)
          ] ?? `Typ ${t}`,
        value: row?.value ?? 0,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults, invoiceExtraTypeTranslations]);

  const getDisplayValue = (row: DefaultValueRow) =>
    localEdits[row.type] ?? row.value;

  const hasRowChanged = (row: DefaultValueRow) => row.type in localEdits;

  const handleChange = (type: InvoiceExtraType, value: string) => {
    const numValue = Number(value);
    if (Number.isNaN(numValue) || numValue < 0) return;

    const serverRow = rows.find((r) => r.type === type);
    if (serverRow && numValue === serverRow.value) {
      setLocalEdits((prev) => {
        const next = { ...prev };
        delete next[type];
        return next;
      });
    } else {
      setLocalEdits((prev) => ({ ...prev, [type]: numValue }));
    }
  };

  const handleSave = async (row: DefaultValueRow) => {
    const data: UpdateInvoiceExtraDefaultValueModel = {
      type: row.type,
      value: getDisplayValue(row),
    };

    setSavingRows((prev) => new Set(prev).add(row.type));
    try {
      await updateCall.mutateAsync({ data });
      setLocalEdits((prev) => {
        const next = { ...prev };
        delete next[row.type];
        return next;
      });
      refetch();
    } catch (error) {
      showException(error);
    } finally {
      setSavingRows((prev) => {
        const next = new Set(prev);
        next.delete(row.type);
        return next;
      });
    }
  };

  const columnSettings = useMemo(
    (): ColumnSetting<DefaultValueRow, object>[] => [
      {
        head: 'Typ',
        width: 200,
        render: (row) => <span>{row.name}</span>,
        sortFunction: (a, b) => a.name.localeCompare(b.name),
      },
      {
        head: 'Standardbelopp (kr)',
        width: 200,
        render: (row) => (
          <Input
            type="number"
            min={0}
            small
            width={120}
            disabled={!isSuperAdmin}
            aria-label={`Standardbelopp för ${row.name}`}
            value={getDisplayValue(row)}
            onChange={(e) => handleChange(row.type, e.target.value)}
          />
        ),
      },
      ...(isSuperAdmin
        ? [
            {
              head: '',
              width: 50,
              render: (row: DefaultValueRow) => (
                <CellRow>
                  {savingRows.has(row.type) ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : hasRowChanged(row) ? (
                    <TextButton onClick={() => handleSave(row)}>
                      <FontAwesomeIcon icon={faSave} />
                    </TextButton>
                  ) : null}
                </CellRow>
              ),
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, localEdits, savingRows, isSuperAdmin]
  );

  if (isLoading) {
    return <FontAwesomeIcon icon={faSpinner} spin />;
  }

  return (
    <Wrapper>
      <h1>
        Standardbelopp för &quot;Fakturera extra&quot;{' '}
        {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
      </h1>
      <Table fluid columnSettings={columnSettings} rows={rows} />
    </Wrapper>
  );
};

export default InvoiceExtraDefaultValueSettings;
